import React from "react";
import "../styles/PrivacyPolicyStyle.css";

const JZCPrivacyPolicy = () => {
  return (
    <div className="jazz_discount_outer">
      <div className="container">
        <div className="discount_inner">
          <h2>JobUp Privacy Policy</h2>

          {/* Section 1: Introduction */}
          <h3>1. Introduction</h3>
          <p>
            EConceptions ("the Company", "we", "us", or "our") provides and operates
            the JobUp platform. The Company controls the information we collect
            for the purposes of applicable laws and regulatory requirements
            directed by the Pakistan Telecommunication Authority ("PTA") or any
            other regulatory body as per the laws of Pakistan (collectively, the
            "Authority").
          </p>
          <br></br>
          <p>
            The Company is committed to providing quality services to you and
            this privacy policy ("Privacy Policy") outlines our ongoing
            obligations to you in respect of how we manage your personal
            information. JobUp knows that you care how information about you is
            used and shared, and we appreciate your trust that we will do so
            carefully and sensibly.
          </p>
          <br></br>
          <p>
            This Privacy Policy describes the types of information we may
            collect from you or that you may provide when you use the JobUp
            website (
            <a
              href="https://JobUp.mobi/home"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://JobUp.mobi/home
            </a>
            ) and mobile application (collectively, the "Platform") and our
            practices for collecting, using, maintaining, protecting, and
            disclosing that information.
          </p>
          <br></br>
          <p>
            Please read this Privacy Policy carefully before you start to use
            the Platform. By accessing or using the Platform, you accept and
            agree to be bound by this Privacy Policy. If you do not agree with
            our policies and practices, you must not access or use the Platform.
          </p>
          <br></br>

          {/* Section 2: Scope and Applicability */}
          <h3>2. Scope and Applicability</h3>
          <p>This Privacy Policy applies to information we collect:</p>
          <ul>
            <li>
              On and through the Platform, including the website and mobile
              application.
            </li>
            <li>
              In email, text, push notification, and other electronic messages
              between you and the Platform.
            </li>
            <li>
              When you interact with our advertising and applications on
              third-party websites and services, if those applications or
              advertising include links to this Privacy Policy.
            </li>
          </ul>
          <p>This Privacy Policy does not apply to information collected by:</p>
          <ul>
            <li>
              The Company offline or through any other means not described in
              this Privacy Policy.
            </li>
            <li>
              Any third party (including our affiliates and subsidiaries),
              including through any application or content (including
              advertising) that may link to or be accessible from or through the
              Platform.
            </li>
          </ul>
          <p>
            The terms used in this Privacy Policy have the same meanings as in
            our Terms and Conditions, which are accessible on the Platform,
            unless otherwise defined in this Privacy Policy.
          </p>
          <br></br>

          {/* Section 3: Definitions */}
          <h3>3. Definitions</h3>
          <p>In this Privacy Policy, unless the context requires otherwise:</p>
          <ul>
            <li>
              <strong>"Personal Information"</strong> means any information that
              directly and/or indirectly and/or in combination with other
              information allows identification of an individual or group of
              individuals. This includes information about individuals even if
              no names or other contact details are collected. For example,
              information relating to an IP address, device identifier, or an
              online identifier may itself be Personal Information even with no
              direct link to a name or contact.
            </li>
            <li>
              <strong>"Log Data"</strong> means data and information collected
              automatically when you use the Platform, including your device
              Internet Protocol ("IP") address, device name, operating system
              version, the configuration of the Platform when utilizing the
              Services, the time and date of your use of the Services, and other
              statistics.
            </li>
            <li>
              <strong>"Location Information"</strong> means information about
              your geographical location collected through one or more of the
              methods described in Section 5.5 of this Privacy Policy.
            </li>
            <li>
              <strong>"Services"</strong> has the meaning given to it in the
              Terms and Conditions.
            </li>
            <li>
              <strong>"User", "you", or "your"</strong> means any individual who
              accesses, browses, or uses the Platform, whether as a guest or a
              registered user.
            </li>
          </ul>
          <br></br>

          {/* Section 4: Your Consent */}
          <h3>4. Your Consent</h3>
          <p>
            If you choose to use the Platform, you agree to the collection and
            use of information in accordance with this Privacy Policy. We will
            not use or share your information with anyone except as described in
            this Privacy Policy.
          </p>
          <br></br>

          <h4>4.1 Deemed Consent</h4>
          <p>
            You are deemed to have given your consent for the collection, use,
            and disclosure of Personal Information in the following
            circumstances:
          </p>
          <ul>
            <li>
              When you voluntarily provide your Personal Information to us.
            </li>
            <li>
              When you are aware of the purposes for which you are providing
              your Personal Information to us.
            </li>
            <li>
              When it is reasonable for you to have provided the Personal
              Information to us in the circumstances.
            </li>
            <li>
              In any other circumstances where consent is deemed under
              applicable data protection legislation in Pakistan.
            </li>
          </ul>
          <p>
            Deemed consent applies to routine data collection that is necessary
            for the delivery of the Services as described in this Privacy
            Policy.
          </p>
          <br></br>

          <h4>4.2 Explicit Consent</h4>
          <p>
            Notwithstanding Section 4.1, the following activities require your
            separate, explicit consent, which will be obtained through a clear
            affirmative action (such as ticking a checkbox, granting a device
            permission, or opting in to a specific feature):
          </p>
          <ul>
            <li>
              Access to sensitive device permissions, including camera,
              microphone, contacts, or location services.
            </li>
            <li>
              Receipt of promotional or marketing communications from the
              Company or authorized third parties.
            </li>
            <li>
              Syncing or importing data from third-party services or cloud
              storage platforms.
            </li>
            <li>
              Disclosure of your Personal Information to third parties for their
              own marketing purposes.
            </li>
          </ul>
          <p>
            You may withdraw your explicit consent for any of the above at any
            time by adjusting your device settings, following the unsubscribe
            instructions in any marketing communication, or contacting us at the
            email address provided in Section 17 below. Withdrawal of consent
            will not affect the lawfulness of any processing carried out prior
            to such withdrawal.
          </p>
          <br></br>

          {/* Section 5: Information Collection and Use */}
          <h3>5. Information Collection and Use</h3>
          <p>
            Upon subscribing to and using the Platform, we collect your Personal
            Information such as sign-up information, your device and its
            technical specification, usage history, associated server logs, your
            transaction history, traffic data, location and internet data. This
            is set out below in detail.
          </p>
          <br></br>

          <h4>5.1 Sign-Up Information</h4>
          <p>
            We collect information you provide when you sign up for an account
            on the Platform. This includes information such as your name, phone
            numbers, date of birth, email addresses, profile picture, and
            password required by us in order to authenticate your identity.
            Where the Platform offers integration with third-party services, you
            may choose to sync certain data such as photos, videos, or cloud
            storage content (e.g., Facebook, Gmail, Dropbox, Instagram). Any
            such sync is entirely optional, requires your explicit action to
            enable, and will only collect data that is necessary for the
            specific feature you have chosen to use.
          </p>
          <br></br>

          <h4>5.2 Log Data</h4>
          <p>
            Whenever you use the Platform, in the case of an error in the
            application or website, we collect Log Data through third-party
            products. This Log Data may include information such as your device
            IP address, device name, operating system version, the configuration
            of the Platform when utilizing the Services, the time and date of
            your use of the Services, and other statistics.
          </p>
          <br></br>

          <h4>5.3 Cookies</h4>
          <p>
            Cookies are files with small amounts of data commonly used as an
            anonymous unique identifier. These are sent to your browser from the
            website that you visit and are stored on your device's internal
            memory.
          </p>
          <p>
            The Platform does not use cookies explicitly. However, the Platform
            may use third-party code and libraries that use cookies to collect
            information and to improve their services. You have the option to
            either accept or refuse these cookies and to know when a cookie is
            being sent to your device. If you choose to refuse cookies, you may
            not be able to use some portions of the Platform.
          </p>
          <p>
            The help feature on most browsers will tell you how to prevent your
            browser from accepting new cookies, how to have the browser notify
            you when you receive a new cookie, or how to disable cookies
            altogether. Because cookies allow you to take advantage of some of
            the Platform's features, we recommend that you leave them turned on.
          </p>
          <br></br>

          <h4>5.4 Device and Technical Usage Information</h4>
          <p>
            We collect information generated by your use of the Platform, such
            as your customer profile, unique identification code, mobile
            carrier, and time zone setting. We also collect device information
            such as operating system, device identifiers, and software version.
            Where a specific feature of the Platform requires it and you have
            granted the relevant permission, we may also collect photos, videos,
            or other media you choose to upload or share through the Platform.
            We do not collect contacts, SMS records, call logs, or app lists
            unless a specific feature of the Platform requires such access and
            you have given your explicit consent to enable that feature.
          </p>
          <p>We use this data to:</p>
          <ul>
            <li>Inform you about your usage statistics.</li>
            <li>
              Assist us in enforcing our Terms and Conditions and policies.
            </li>
            <li>
              Improve the Platform and administer internal operations (such as
              troubleshooting, data analysis, testing, research, statistical,
              and survey purposes).
            </li>
            <li>
              Keep the Platform safe and secure (including verifying accounts
              and activity).
            </li>
            <li>Comply with legal obligations.</li>
          </ul>
          <br></br>

          <h4>5.5 Location Information</h4>
          <p>
            The Platform collects Location Information based on one or more of
            the following:
          </p>
          <ul>
            <li>
              Derivations made from the IP address from which you connect.
            </li>
            <li>
              The Global Positioning System (GPS) of your device when the
              Platform is running in the foreground or background.
            </li>
          </ul>
          <p>
            We use your Location Information to provide you with location-based
            tools and functions, provide customized nearby services or
            value-added services (including marketing) that we think you would
            find useful. If you do not wish to share your GPS location with us,
            you can switch off the GPS functionality on your mobile device.
          </p>
          <br></br>

          <h4>5.6 Transaction Information</h4>
          <p>
            We collect information based on the subscription plan you purchase
            via the Platform so we can tailor the Services for you. This
            transaction-related data will only be used for value-added services,
            including marketing, with your consent.
          </p>
          <br></br>

          <h4>5.7 Your Usage Information</h4>
          <p>
            We collect your usage data, such as statistics about how you use the
            Platform, to ensure that the Platform is easy and enjoyable to use.
          </p>
          <br></br>

          <h4>5.8 Billing Information</h4>
          <p>
            We may collect billing information, including payment details,
            credit history, bank account information, and mobile account
            details, as necessary to process subscription fees through the
            applicable billing provider.
          </p>
          <br></br>

          <h4>5.9 Other Ad-Hoc Information</h4>
          <p>
            We may conduct surveys or obtain other feedback, comments, and input
            from you via the Platform or through other channels as deemed
            necessary. We use this information to understand your needs, to
            identify what is working well, and to determine what improvements
            can be made to enhance your user experience. We may use such
            information to provide marketing communications to you.
          </p>
          <br></br>

          <h4>5.10 Automatic Data Collection Technologies</h4>
          <p>
            As you navigate through and interact with the Platform, we may use
            automatic data collection technologies to collect certain
            information about your equipment, browsing actions, and patterns,
            including:
          </p>
          <ul>
            <li>
              Details of your visits to the Platform, including traffic data,
              location data, logs, and other communication data and the
              resources that you access and use.
            </li>
            <li>
              Information about your computer or mobile device and internet
              connection, including your IP address, operating system, and
              browser type.
            </li>
          </ul>
          <p>
            The information we collect automatically may include Personal
            Information, or we may maintain it or associate it with Personal
            Information we collect in other ways or receive from third parties.
            It helps us to improve the Platform and to deliver a better and more
            personalized service, including by enabling us to estimate our
            audience size and usage patterns, store information about your
            preferences, speed up your searches, and recognize you when you
            return.
          </p>
          <br></br>

          <h4>5.11 Email and Electronic Communications</h4>
          <p>
            To help us make emails and other electronic communications more
            useful and interesting, we may receive a confirmation when you open
            an email from us if your device supports such capabilities.
          </p>
          <br></br>

          <h4>5.12 Information from Other Sources</h4>
          <p>
            We might receive information about you from other sources, including
            affiliated businesses, billing providers, and third-party service
            providers, and add it to your account information.
          </p>
          <br></br>

          <h4>5.13 Aggregated and Anonymized Data</h4>
          <p>
            We will use any of the above information to create aggregated and
            anonymized data, insights, and reports for internal business
            planning purposes and to understand how to improve the Platform for
            our Users.
          </p>
          <p>
            We will also use the above-mentioned information to contact you and
            inform you regarding any new developments, features, or other
            important information about the Platform.
          </p>
          <br></br>

          <h4>5.14 Device Permissions</h4>
          <p>
            Certain features of the Platform may request access to specific
            permissions on your device. For example, camera access may be
            requested for profile photo uploads, and microphone access may be
            requested if the Platform offers audio-based features. Each
            permission request will be presented to you by your device's
            operating system, and you may accept or decline it at your
            discretion. Declining a permission may limit your ability to use the
            specific feature that requires it but will not prevent you from
            using the rest of the Platform. You can review and revoke any
            permission at any time through your device settings.
          </p>
          <br></br>

          {/* Section 6: How We Use Your Information */}
          <h3>6. How We Use Your Information</h3>
          <p>
            We use information that we collect about you or that you provide to
            us, including any Personal Information:
          </p>
          <ul>
            <li>To present the Platform and its contents to you.</li>
            <li>
              To provide you with information, products, or services that you
              request from us.
            </li>
            <li>To fulfil any other purpose for which you provide it.</li>
            <li>
              To carry out our obligations and enforce our rights arising from
              any contracts entered into between you and us, including for
              billing and collection.
            </li>
            <li>
              To notify you about changes to the Platform or any products or
              services we offer or provide through it.
            </li>
            <li>
              To allow you to participate in interactive features on the
              Platform.
            </li>
            <li>
              To personalize and continually improve your experience on the
              Platform.
            </li>
            <li>
              To respond to your requests, customize your experience, and
              communicate with you.
            </li>
            <li>
              To use information from your interactions with our customer
              service channels to resolve issues and for future communications
              related to your account.
            </li>
            <li>
              In any other way we may describe when you provide the information.
            </li>
            <li>For any other purpose with your consent.</li>
          </ul>
          <p>
            You agree and acknowledge that you consent to the Company using your
            Personal Information for the purpose of promotional activities,
            marketing communications, service-related offers, and updates from
            the Company or any authorized third-party service provider engaged
            by the Company for such purposes. If you believe that a caller or
            message sender is infringing upon your privacy, you may use the
            call-blocking feature or any applicable privacy settings available
            on your device to restrict such communications.
          </p>
          <br></br>

          {/* Section 7: Disclosure of Your Information */}
          <h3>7. Disclosure of Your Information</h3>
          <p>
            Information about our Users is an important part of our business,
            and we are not in the business of selling it to others.
          </p>
          <br></br>

          <h4>7.1 Aggregated Information</h4>
          <p>
            We may disclose aggregated information about our Users, and
            information that does not identify any individual, without
            restriction.
          </p>
          <br></br>

          <h4>7.2 Disclosure of Personal Information</h4>
          <p>
            We may disclose Personal Information that we collect or that you
            provide as described in this Privacy Policy:
          </p>
          <ul>
            <li>To our subsidiaries and affiliates.</li>
            <li>
              To contractors, service providers, and other third parties we use
              to support our business, who are obligated not to disclose or use
              the information for any other purpose.
            </li>
            <li>
              To the applicable billing provider for the purpose of processing
              subscription fees and managing your account.
            </li>
            <li>
              To third parties to market their products or services to you if
              you have consented to such disclosures.
            </li>
            <li>To fulfil the purpose for which you provide it.</li>
            <li>
              For any other purpose disclosed by us when you provide the
              information.
            </li>
            <li>With your consent.</li>
          </ul>
          <br></br>

          <h4>7.3 Affiliated Businesses</h4>
          <p>
            We work closely with affiliated businesses. In some cases, we
            operate stores, provide services, or manage product lines jointly
            with businesses such as telecom operators. You can tell when a third
            party is involved in your transactions, and we share customer
            information related to those transactions with that third party.
          </p>
          <br></br>

          <h4>7.4 Promotional Offers</h4>
          <p>
            Sometimes we send offers to selected groups of Users on behalf of
            other businesses. When we do this, we do not give that business your
            name and address.
          </p>
          <br></br>

          <h4>7.5 Legal and Safety Disclosures</h4>
          <p>We may also disclose your Personal Information:</p>
          <ul>
            <li>
              To comply with any court order, law, or legal process, including
              to respond to any government or regulatory request.
            </li>
            <li>
              To enforce or apply our Terms and Conditions and other agreements.
            </li>
            <li>
              To protect the rights, property, or safety of the Company, our
              Users, or others. This includes exchanging information with other
              companies and organizations for fraud protection and credit risk
              reduction.
            </li>
          </ul>
          <p>
            This does not include selling, renting, sharing, or otherwise
            disclosing personally identifiable information from Users for
            commercial purposes in violation of the commitments set forth in
            this Privacy Policy.
          </p>
          <br></br>

          {/* Section 8: Limiting Collection, Use, and Disclosure of Personal Information */}
          <h3>
            8. Limiting Collection, Use, and Disclosure of Personal Information
          </h3>
          <p>
            The Company collects Personal Information primarily from its Users
            (including prospective Users). The collection of Personal
            Information is limited to that which is necessary for the identified
            purposes. Unless permitted under law, the Company will not disclose
            Personal Information to other persons or entities for the
            advertising, promotion, or marketing of such other party's products
            and services without your consent, and the Company will not sell
            Personal Information to anyone for any unauthorized purpose.
          </p>
          <p>
            The primary uses of data collected through the Platform are service
            delivery, customer personalization, analytics, and reporting. Data
            may also be shared with the categories of recipients described in
            Section 7 (including billing providers, service providers,
            affiliates, and government or regulatory authorities where required
            by law) strictly for the purposes set out in this Privacy Policy.
            All data held by the Company is protected using encryption and other
            commercially reasonable security measures.
          </p>
          <br></br>

          {/* Section 9: Data Security */}
          <h3>9. Data Security</h3>
          <p>
            We have implemented commercially reasonable measures designed to
            secure your Personal Information from accidental loss and from
            unauthorized access, use, alteration, and disclosure. We work to
            protect the security of your information during transmission by
            using encryption technologies.
          </p>
          <p>
            The safety and security of your information also depends on you.
            Where we have given you (or where you have chosen) credentials such
            as a password, PIN, or other access information to certain parts of
            the Platform, you are responsible for keeping these credentials
            confidential. We ask you not to share your credentials with anyone.
            You should use particular caution when accessing your account from a
            public or shared device so that others are not able to view or
            record your password or other personal data. It is important for you
            to protect against unauthorized access to your password and to your
            devices, and to sign off when you have finished using a shared
            device.
          </p>
          <p>
            Unfortunately, the transmission of information via the internet is
            not completely secure. Although we do our best to protect your
            Personal Information, we cannot guarantee the security of your
            Personal Information transmitted to the Platform. Any transmission
            of Personal Information is at your own risk. We are not responsible
            for circumvention of any privacy settings or security measures
            contained on the Platform.
          </p>
          <br></br>

          {/* Section 10: Data Retention */}
          <h3>10. Data Retention</h3>
          <p>
            We will collect, use, and store your Personal Information as per our
            legal and regulatory requirements. We retain Personal Information
            for as long as your account is active or as needed to provide you
            the Services, and for a reasonable period thereafter as necessary to
            comply with our legal obligations, resolve disputes, and enforce our
            agreements.
          </p>
          <p>
            It is important to us that your Personal Information is up to date.
            We will take reasonable steps to make sure that your Personal
            Information is accurate, complete, and up to date. If you find that
            the information we have is not up to date or is inaccurate, please
            advise us as soon as practicable so we can update our records and
            ensure we can continue to provide quality services to you.
          </p>
          <br></br>

          {/* Section 11: Deletion of Data */}
          <h3>11. Deletion of Data</h3>
          <p>
            If you want the data collected while you used the Platform to be
            deleted, you may unsubscribe or log out of the Services via the
            application or website or submit a deletion request by contacting us
            at the email address provided in Section 17 below.
          </p>
          <p>
            Upon receiving a valid deletion request or upon your subscription,
            we will delete or anonymize your Personal Information from our
            active systems within a reasonable timeframe. However, please note
            that we may be required to retain certain information for the
            following purposes, even after your account is closed:
          </p>
          <ul>
            <li>
              Billing and transaction records, as required for accounting and
              audit purposes.
            </li>
            <li>
              Records necessary to comply with applicable legal, regulatory, or
              tax obligations.
            </li>
            <li>
              Information reasonably necessary for fraud prevention, dispute
              resolution, and enforcement of our Terms and Conditions.
            </li>
            <li>
              Data stored in backup or archival systems, which will be deleted
              in accordance with our standard data retention and backup cycles.
            </li>
          </ul>
          <p>
            Once all applicable retention periods have expired, your remaining
            data will be permanently deleted. If you visit or use the Services
            again after deletion, you will be treated as a new user.
          </p>
          <br></br>

          {/* Section 12: Your Rights and Access to Information */}
          <h3>12. Your Rights and Access to Information</h3>
          <p>
            The Platform gives you access to a broad range of information about
            your account and your interactions with the Platform for the limited
            purpose of viewing and, in certain cases, updating that information.
          </p>
          <p>Subject to applicable law, you have the right to:</p>
          <ul>
            <li>Access the Personal Information we hold about you.</li>
            <li>
              Request correction of any inaccurate or incomplete Personal
              Information.
            </li>
            <li>
              Request deletion of your Personal Information, subject to our
              legal and regulatory obligations.
            </li>
            <li>
              Object to or restrict certain processing of your Personal
              Information.
            </li>
            <li>
              Withdraw your consent to any processing based on consent, without
              affecting the lawfulness of processing carried out prior to such
              withdrawal.
            </li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the details
            provided in Section 17 below.
          </p>
          <br></br>

          {/* Section 13: Children's Privacy */}
          <h3>13. Children's Privacy</h3>
          <p>
            The Platform is not directed to children under the age of eighteen
            (18). If you are under 18, you may use the Platform only with the
            involvement and consent of a parent or legal guardian. We do not
            knowingly collect Personal Information from children under 18
            without parental consent. If we become aware that we have collected
            Personal Information from a child under 18 without verification of
            parental consent, we will take steps to remove that information from
            our systems.
          </p>
          <br></br>

          {/* Section 14: Third-Party Service Providers */}
          <h3>14. Third-Party Service Providers</h3>
          <p>
            We may employ third-party companies and individuals for the
            following reasons:
          </p>
          <ul>
            <li>To facilitate the Services.</li>
            <li>To provide the Services on our behalf.</li>
            <li>
              To perform service-related functions (including fulfilling orders,
              delivering communications, removing repetitive information from
              customer lists, analyzing data, providing marketing assistance,
              providing search results and links, processing payments, and
              providing customer service).
            </li>
            <li>To assist us in analyzing how the Platform is used.</li>
          </ul>
          <p>
            These third parties may have access to your Personal Information
            solely to perform the tasks assigned to them on our behalf. They are
            contractually obligated not to disclose or use the information for
            any other purpose.
          </p>
          <p>
            The Platform may also use third-party services that may collect
            information used to identify you. We encourage you to review the
            privacy policies of any third-party services you interact with
            through the Platform.
          </p>
          <br></br>

          {/* Section 15: Links to Other Sites */}
          <h3>15. Links to Other Sites</h3>
          <p>
            The Platform may contain links to other sites. If you click on a
            third-party link, you will be directed to that site. Note that these
            external sites are not operated by us. Therefore, we strongly advise
            you to review the privacy policy of those websites. We have no
            control over and assume no responsibility for the content, privacy
            policies, or practices of any third-party sites or services.
          </p>
          <p>
            We do not currently have third-party advertisers on the Platform.
            However, if we opt for such third-party advertising in the future,
            the same will be covered in an updated version of this Privacy
            Policy and details will be added accordingly.
          </p>
          <br></br>

          {/* Section 16: Changes to This Privacy Policy */}
          <h3>16. Changes to This Privacy Policy</h3>
          <p>
            We may update this Privacy Policy from time to time. It is our
            policy to post any changes on this page. The date the Privacy Policy
            was last revised is identified at the top of the page. You are
            responsible for periodically visiting the Platform and this Privacy
            Policy to check for any changes. Your continued use of the Platform
            after we post changes constitutes your acceptance of those changes.
          </p>
          <p>
            Our business changes constantly, and this Privacy Policy may change
            also. We may email periodic reminders of our policies, but you
            should check the Platform frequently to see recent changes. Unless
            stated otherwise, the current Privacy Policy applies to all
            information that we have about you and your account. We stand behind
            the promises we make and will never materially change our policies
            and practices to make them less protective of customer information
            collected in the past without the consent of affected customers.
          </p>
          <br></br>

          {/* Section 17: Contact Us */}
          <h3>17. Contact Us</h3>
          <p>
            If you have any questions, suggestions, or concerns about this
            Privacy Policy, or if you wish to exercise any of your rights
            described herein, please do not hesitate to contact us at:
          </p>
          <p>
            <strong>JobUp (operated by eConceptions)</strong>
            <br></br>
            Email:{" "}
            <a href="mailto:support@econceptions.mobi">
              support@econceptions.mobi
            </a>
            <br></br>
            Website:{" "}
            <a
              href="https://JobUp.mobi/home"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://JobUp.mobi/home
            </a>
          </p>
          <p>
            If you have any concern about privacy at the Platform, please
            contact us with a thorough description, and we will try to resolve
            it.
          </p>
          <br></br>

          {/* Section 18: Governing Law */}
          <h3>18. Governing Law</h3>
          <p>
            This Privacy Policy shall be governed by and construed in accordance
            with the laws of the Islamic Republic of Pakistan. Any disputes
            arising under or in connection with this Privacy Policy shall be
            resolved in accordance with the dispute resolution provisions set
            out in the Terms and Conditions.
          </p>
          <br></br>
        </div>
      </div>
    </div>
  );
};

export default JZCPrivacyPolicy;
